import {memo, useRef, useCallback, useEffect, useLayoutEffect} from 'react'
import {useConversationMessages} from '@/features/conversations/query/useConversationMessages'
import {useSendConversationMessage} from '@/features/conversations/query/useSendConversationMessage'
import {useMarkConversationAsReadOnOpen} from '@/features/conversations/hooks/useMarkConversationAsReadOnOpen'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {useTranslation} from 'react-i18next'
import {ScrollArea} from '@/components/ui/scroll-area/ScrollArea'
import {Scrollbar} from '@/components/ui/scrollbar/Scrollbar'
import {Spinner} from '@/components/ui/spinner/Spinner'
import {StyledMessagesContainer, StyledMessagesScrollArea, StyledMessagesBody, StyledLoadMoreIndicator} from './style'
import {MessageGroup} from '../message-group/MessageGroup'
import {EmptyConversationState} from '../empty-conversation-state/EmptyConversationState'
import {ConversationMessagesSkeleton} from '../conversation-messages-skeleton/ConversationMessagesSkeleton'
import {NewMessageForm} from '../new-message-form/NewMessageForm'

const ConversationMessagesComponent = ({
    conversationId,
    hasUnreadMessages,
    patientId
}: {
    conversationId: string
    hasUnreadMessages?: boolean | null
    /** Patient scope for the conversations, or null for internal conversations (no patient) */
    patientId: string | null
}) => {
    const {t} = useTranslation()
    const {messages, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage} =
        useConversationMessages(conversationId)
    const {mutate: sendMessage, isPending: isSending} = useSendConversationMessage()
    const scrollViewportRef = useRef<HTMLDivElement>(null)
    const observerTarget = useRef<HTMLDivElement>(null)
    // Tracks whether the next `messages` update comes from loading older messages
    // (scroll position must be preserved) rather than the initial load or a new
    // message being sent (scroll to bottom in both of those cases).
    const isPaginatingRef = useRef(false)
    const prevScrollHeightRef = useRef(0)

    // Automatically mark conversation as read when opened with unread messages
    useMarkConversationAsReadOnOpen(patientId, conversationId, hasUnreadMessages)

    const handleFetchNextPage = useCallback(() => {
        if (scrollViewportRef.current) {
            prevScrollHeightRef.current = scrollViewportRef.current.scrollHeight
        }
        isPaginatingRef.current = true
        fetchNextPage()
    }, [fetchNextPage])

    const handleIntersection = useCallback(
        (entries: IntersectionObserverEntry[]) => {
            const [entry] = entries
            if (entry.isIntersecting && hasNextPage && !isFetchingNextPage) {
                handleFetchNextPage()
            }
        },
        [handleFetchNextPage, hasNextPage, isFetchingNextPage]
    )

    // Set up intersection observer for infinite scrolling at the top
    useEffect(() => {
        const observer = new IntersectionObserver(handleIntersection, {
            root: null,
            rootMargin: '20px',
            threshold: 0.1
        })

        if (observerTarget.current) {
            observer.observe(observerTarget.current)
        }

        return () => {
            observer.disconnect()
        }
    }, [handleIntersection])

    // Scroll to bottom on initial load or when a new message arrives; when older
    // messages are prepended via pagination, keep the currently viewed messages in place instead.
    useLayoutEffect(() => {
        const viewport = scrollViewportRef.current
        if (!viewport || !messages || messages.length === 0) return

        if (isPaginatingRef.current) {
            viewport.scrollTop = viewport.scrollHeight - prevScrollHeightRef.current
            isPaginatingRef.current = false
        } else {
            viewport.scrollTop = viewport.scrollHeight
        }
    }, [messages])

    const handleSendMessage = (data: {message: string; isUrgent: boolean}) => {
        sendMessage({conversationId, message: data.message, isUrgent: data.isUrgent})
    }

    return (
        <StyledMessagesContainer direction="column" fullWidth>
            <StyledMessagesScrollArea>
                <ScrollArea style={{height: '100%'}} scrollbar={<Scrollbar />} viewportProps={{ref: scrollViewportRef}}>
                    <StyledMessagesBody gap={2} direction="column" justify="flex-start">
                        {isLoading ? (
                            <ConversationMessagesSkeleton />
                        ) : (
                            <>
                                {hasNextPage && (
                                    <StyledLoadMoreIndicator ref={observerTarget}>
                                        {isFetchingNextPage ? (
                                            <Flexbox align="center" justify="center" gap={2}>
                                                <Spinner size={16} />
                                                {t('conversations:loading_more')}
                                            </Flexbox>
                                        ) : (
                                            t('conversations:scroll_up_for_more')
                                        )}
                                    </StyledLoadMoreIndicator>
                                )}

                                {messages && messages.length > 0 ? (
                                    <MessageGroup messages={messages} />
                                ) : (
                                    <EmptyConversationState />
                                )}
                            </>
                        )}
                    </StyledMessagesBody>
                </ScrollArea>
            </StyledMessagesScrollArea>

            <NewMessageForm onSubmit={handleSendMessage} isLoading={isLoading} isSending={isSending} />
        </StyledMessagesContainer>
    )
}

export const ConversationMessages = memo(ConversationMessagesComponent)
